import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import AppModal from "../components/AppModal";
import { Accordion } from "react-bootstrap";
import {
  Plus,
  Clock,
  Calendar as CalendarIcon,
  ArrowRight,
  Timer,
  CalendarDays,
  X,
} from "lucide-react";
import { useGlobal } from "../context/GlobalContext";
import { formatDateMMDDYYYY, formatUtcToLocalTime } from "../utils/timezone";
import CalendarPicker from "../components/Calender";

const WorkLog = () => {
  const { getActivityList, createManualLog } = useGlobal();
  const [showModal, setShowModal] = useState(false);
  const [workLogs, setWorkLogs] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const [expandedJobs, setExpandedJobs] = useState(new Set());
  const TITLE_TRUNCATE_LIMIT = 60;

  const calendarRef = useRef(null);
  const dropRef = useRef(null);

  const toggleJobExpand = (jobId) => {
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const formatLogs = (res) =>
    res.map((day) => ({
      activityDate: day.activity_date,
      date: formatDateMMDDYYYY(day.activity_date),
      totalTime: day.total_time,
      jobs: day.items.map((item) => ({
        id: item.activity_pid,
        title: item.title,
        time: item.total_time,
        fromTime: formatUtcToLocalTime(item.from_time),
        toTime: formatUtcToLocalTime(item.to_time),
      })),
    }));

  useEffect(() => {
    const loadData = async () => {
      const res = await getActivityList();
      if (res) setWorkLogs(formatLogs(res));
    };
    loadData();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const inTrigger = calendarRef.current?.contains(e.target);
      const inDrop = dropRef.current?.contains(e.target);
      if (!inTrigger && !inDrop) setShowCalendar(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleCalToggle = () => {
    if (!showCalendar && calendarRef.current) {
      const rect = calendarRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 6, left: rect.left });
    }
    setShowCalendar((v) => !v);
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const toIsoDate = (mmddyyyy) => {
    if (!mmddyyyy) return "";
    const [m, d, y] = mmddyyyy.split("/");
    return `${y}-${m}-${d}`;
  };

  const filteredLogs = filterDate
    ? workLogs.filter((log) => toIsoDate(log.activityDate) === filterDate)
    : workLogs;

  return (
    <div className="content-part">
      {/* Header */}
      {workLogs.length > 0 && (
        <div className="row g-2 align-items-center mb-3">
          {/* Date Filter */}
          <div className="col-6 col-md-4" ref={calendarRef}>
            <div
              className={`db-cal-wrap${filterDate ? " has-value" : ""}`}
              onClick={handleCalToggle}
            >
              <CalendarDays size={14} className="db-cal-icon" />

              <span className="db-cal-label">
                {filterDate ? formatDisplayDate(filterDate) : "Pick date"}
              </span>

              {filterDate && (
                <button
                  className="db-cal-clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    setFilterDate("");
                    setShowCalendar(false);
                  }}
                  aria-label="Clear date filter"
                >
                  <X size={11} />
                </button>
              )}
            </div>
          </div>

          {/* Create Log Button */}
          <div className="col-6 col-md-8">
            <div className="d-flex justify-content-end">
              <button
                className="btn btn-secondary d-flex align-items-center gap-1"
                onClick={() => setShowModal(true)}
              >
                <Plus size={15} />
                <span className="">Create Log</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      {workLogs.length === 0 ? (
        <div className="wl-empty">
          <div className="wl-empty-icon">
            <Clock size={28} />
          </div>
          <p className="wl-empty-title">No activity logs yet</p>
          <p className="wl-empty-sub">
            Click &ldquo;Create Log&rdquo; to add your first entry.
          </p>
          <button
            className="btn btn-secondary d-flex align-items-center gap-1 mt-2"
            onClick={() => setShowModal(true)}
          >
            <Plus size={15} />
            Create Log
          </button>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="wl-empty">
          <div className="wl-empty-icon">
            <CalendarIcon size={28} />
          </div>
          <p className="wl-empty-title">
            No logs for {formatDisplayDate(filterDate)}
          </p>
          <p className="wl-empty-sub">
            Try a different date or clear the filter.
          </p>
        </div>
      ) : (
        <Accordion defaultActiveKey="0" className="wl-accordion">
          {filteredLogs.map((log, index) => (
            <Accordion.Item
              eventKey={index.toString()}
              key={log.date}
              className="wl-item"
            >
              <Accordion.Header>
                <div className="wl-acc-header">
                  <div className="wl-acc-date">
                    <span className="wl-acc-date-icon">
                      <CalendarIcon size={13} />
                    </span>
                    {log.date}
                  </div>
                  <span className="wl-acc-total">
                    <Timer size={12} />
                    {log.totalTime}
                  </span>
                </div>
              </Accordion.Header>

              <Accordion.Body className="p-0">
                <div className="wl-jobs">
                  {log.jobs.map((job, i) => (
                    <div
                      key={job.id}
                      className={`wl-job${i < log.jobs.length - 1 ? " wl-job--sep" : ""}`}
                    >
                      <div className="wl-job-info">
                        <p
                          className={`wl-job-title${expandedJobs.has(job.id) ? " wl-job-title--expanded" : ""}`}
                        >
                          {job.title}
                        </p>
                        {job.title &&
                          job.title.length > TITLE_TRUNCATE_LIMIT && (
                            <button
                              type="button"
                              className="wl-job-title-toggle"
                              onClick={() => toggleJobExpand(job.id)}
                            >
                              {expandedJobs.has(job.id)
                                ? "View less"
                                : "View more"}
                            </button>
                          )}
                        {(job.fromTime || job.toTime) && (
                          <div className="wl-job-times">
                            <span>{job.fromTime || "—"}</span>
                            <ArrowRight size={10} />
                            <span>{job.toTime || "—"}</span>
                          </div>
                        )}
                      </div>
                      <span className="wl-job-dur">{job.time}</span>
                    </div>
                  ))}
                </div>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {showCalendar &&
        createPortal(
          <div
            ref={dropRef}
            className="db-cal-drop"
            style={{ top: dropPos.top, left: dropPos.left }}
          >
            <CalendarPicker
              mode="default"
              value={filterDate ? filterDate.replace(/-/g, "/") : undefined}
              onDateChange={(dateObj) => {
                const yyyy = dateObj.getFullYear();
                const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                const dd = String(dateObj.getDate()).padStart(2, "0");
                setFilterDate(`${yyyy}-${mm}-${dd}`);
                setShowCalendar(false);
              }}
            />
          </div>,
          document.body,
        )}

      <AppModal
        type="log-manual-time"
        show={showModal}
        onClose={() => setShowModal(false)}
        onSubmit={async (data) => {
          const res = await createManualLog(data);
          if (res) {
            const updated = await getActivityList();
            if (updated) setWorkLogs(formatLogs(updated));
          }
        }}
      />
    </div>
  );
};

export default WorkLog;
